import {
    type App,
    inject,
    onBeforeUnmount,
    type Ref,
    ref,
    toValue,
    watch,
} from 'vue'
import Emittery from 'emittery'
import type { RefOrGetter } from '@/helpers'

const key = 'app.plugin.event-bus'
const keySharedSate = 'app.plugin.shared-state'

type EventMap = {
    'open-filter-modal': undefined
    activeFilterCount: number
}

type EventsWithDataKeys<Map> = {
    [Key in keyof Map]: Map[Key] extends undefined ? never : Key
}[keyof Map]
type EventsWithData = {
    [Key in EventsWithDataKeys<EventMap>]: EventMap[Key]
}

export function installEventBus(
    app: App,
    abortController: AbortController
): Emittery<EventMap> {
    const emitter = new Emittery<EventMap>()

    const on = emitter.on.bind(emitter)

    emitter.on = (eventName, listener, options) =>
        on(eventName, listener, {
            signal: abortController.signal,
            ...options,
        })

    app.provide(key, emitter)

    const sharedState: Partial<EventsWithData> = {}

    app.provide(keySharedSate, sharedState)

    return emitter
}

export function useEventBus(): Emittery<EventMap> {
    return inject(key) as Emittery<EventMap>
}

function useSharedState(): Partial<EventsWithData> {
    return inject(keySharedSate) as Partial<EventsWithData>
}

export function usePush(
    event: keyof EventsWithData,
    data: RefOrGetter<EventsWithData[typeof event]>
): void {
    const bus = useEventBus()
    const state = useSharedState()

    bus.emit(event, toValue(data))
    state[event] = toValue(data)

    watch(data, (val) => {
        bus.emit(event, val)
        state[event] = val
    })
}

export function usePull(
    event: keyof EventsWithData,
    defaultValue: EventsWithData[typeof event]
): Ref<EventsWithData[typeof event]> {
    const state = useSharedState()
    const bus = useEventBus()

    const initialValue: EventsWithData[typeof event] = state.hasOwnProperty(
        event
    )
        ? // Somebody must have already emitted an event if the object has this key
          // So this "has" to be okay
          state[event]!
        : defaultValue
    const value = ref(initialValue)

    bus.on(event, (val) => {
        value.value = val
    })

    return value
}

export function useListen(
    event: keyof EventMap,
    handler: (value: EventMap[typeof event]) => void
): void {
    const bus = useEventBus()

    bus.on(event, handler)

    onBeforeUnmount(() => {
        bus.off(event, handler)
    })
}
